import React, { useEffect, useRef, lazy, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight } from 'lucide-react';
import LeadForm from '../components/LeadForm';
import VideoBackground from '../components/VideoBackground';
import { scrollToHash } from '../lib/scrollToHash';

// Below-the-fold sections are code-split so they don't bloat the initial
// homepage bundle or block the hero from painting.
const SocialProof = lazy(() => import('../components/SocialProof'));
const TransformationShowcase = lazy(() => import('../components/TransformationShowcase'));
const DisciplinesShowcase = lazy(() => import('../components/DisciplinesShowcase'));
const FacilitiesCarousel = lazy(() => import('../components/FacilitiesCarousel'));
const UpcomingEvents = lazy(() => import('../components/UpcomingEvents'));
const MembershipPerks = lazy(() => import('../components/MembershipPerks'));
const FoundingPromoBand = lazy(() => import('../components/FoundingPromoBand'));

// Temporary Sopan Baug launch promo at the top of the homepage. Set to false to hide.
const SHOW_FOUNDING_PROMO = false;

// Rename import for clarity but keep using same component

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    
    // Handle ?scroll=form parameter
    if (searchParams.get('scroll') === 'form') {
      setTimeout(() => {
        document.getElementById('lead-form-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
    
    // Handle scrollTarget from session storage (set by navbar when navigating from other pages)
    const scrollTarget = sessionStorage.getItem('scrollTarget');
    if (scrollTarget) {
      sessionStorage.removeItem('scrollTarget');
      scrollToHash(scrollTarget);
    }
    
    // Handle direct hash in URL (e.g., /#events)
    if (location.hash) {
      scrollToHash(location.hash);
    }
  }, [location]);

  return (
    <div className="bg-cobra-black min-h-screen">
      {/* TEMPORARY: Sopan Baug Founding 100 launch promo */}
      {SHOW_FOUNDING_PROMO && <Suspense fallback={null}><FoundingPromoBand /></Suspense>}
      {/* Hero Section with Video Background */}
      <section ref={heroRef} data-testid="hero-section">
        <h1 className="sr-only">Cobra Thai — Premier MMA, Muay Thai, Boxing & Brazilian Jiu-Jitsu Academy in Kharadi, Pune</h1>
        <VideoBackground
          videoUrl="/cobra-hero.mp4"
          posterUrl="/cobra-hero-poster.jpg"
          className="h-screen flex items-center justify-center"
        >
          <motion.div 
            style={{ y, opacity }}
            className="text-center px-4 max-w-6xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="font-anton text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-white uppercase tracking-wider mb-6 leading-tight"
              data-testid="hero-title"
            >
              <span className="block mb-2">WELCOME TO THE</span>
              <span className="text-cobra-red text-shadow-glow block" style={{ textShadow: '0 0 40px rgba(164, 0, 3, 0.8), 0 0 80px rgba(164, 0, 3, 0.4)' }}>
                SNAKE PIT
              </span>
            </motion.div>
            
            <motion.p
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
              className="font-manrope text-lg sm:text-xl md:text-2xl text-white/90 mb-10 tracking-wide max-w-3xl mx-auto leading-relaxed"
              data-testid="hero-subtitle"
            >
              Pune's premiere MMA & fitness COMMUNITY<br className="sm:hidden" />
              <span className="sm:hidden"> </span><br className="hidden sm:block" />
              Now with a new & bigger facility
            </motion.p>
            
            <motion.button
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.6, ease: "easeOut" }}
              whileHover={{ scale: 1.05, boxShadow: '0 0 40px rgba(164, 0, 3, 0.6)' }}
              whileTap={{ scale: 0.95 }}
              onClick={() => document.getElementById('lead-form-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-cobra-red text-white hover:bg-red-800 uppercase font-bold tracking-wider rounded-sm px-8 sm:px-12 py-4 sm:py-5 text-base sm:text-lg transition-all duration-300 inline-flex items-center space-x-3 box-shadow-glow"
              data-testid="hero-cta-button"
            >
              <span>GET YOUR 1 DAY FREE TRIAL</span>
              <ArrowRight />
            </motion.button>
          </motion.div>
        </VideoBackground>
      </section>

      {/* Lead Form Section - Right After Hero */}
      <section id="lead-form-section" className="py-8 md:py-32 px-4 md:px-8 lg:px-12 bg-gradient-to-b from-cobra-black via-cobra-dark to-cobra-black" data-testid="lead-form-section">
        <div className="max-w-2xl mx-auto text-center">
          <AnimatedSection>
            <h2 className="font-anton text-4xl md:text-5xl lg:text-6xl text-white uppercase tracking-wider mb-6" data-testid="form-section-title">
              START YOUR <span className="text-cobra-red text-shadow-glow">JOURNEY</span>
            </h2>
            <p className="font-manrope text-white/80 text-lg md:text-xl mb-12" data-testid="form-section-subtitle">
              Sign up with your details to receive your 1 day free pass
            </p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-cobra-dark/80 backdrop-blur-md border border-white/10 p-8 md:p-12 rounded-sm"
            >
              <LeadForm campaign="home-page" enableThankYouPage={true} thankYouSlug="free-trial" />
            </motion.div>
          </AnimatedSection>
        </div>
      </section>

      {/* Disciplines Showcase - New Design */}
      <Suspense fallback={<div className="min-h-[40vh]" />}>
        <DisciplinesShowcase />

        {/* Upcoming Events - Dynamically loaded from admin */}
        <UpcomingEvents />

        {/* Facilities Carousel - Auto-scrolling */}
        <FacilitiesCarousel />

        {/* Social Proof Counters */}
        <SocialProof />

        {/* Transformation Showcase */}
        <TransformationShowcase />

        {/* What You Get When You Join */}
        <MembershipPerks />
      </Suspense>

      {/* CTA Banner */}
      <section className="relative py-32 px-4 md:px-8 lg:px-12 overflow-hidden" data-testid="cta-banner">
        <div className="absolute inset-0 bg-gradient-to-r from-cobra-black via-cobra-red/20 to-cobra-black" />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <AnimatedSection>
            <h2 className="font-anton text-4xl md:text-6xl lg:text-7xl text-white uppercase tracking-wider mb-8 leading-tight">
              READY TO <span className="text-cobra-red text-shadow-glow">DOMINATE?</span>
            </h2>
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: '0 0 40px rgba(164, 0, 3, 0.6)' }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/membership')}
              className="bg-cobra-red text-white hover:bg-red-800 uppercase font-bold tracking-wider rounded-sm px-10 py-5 text-lg transition-all duration-300 inline-flex items-center space-x-3 box-shadow-glow"
              data-testid="cta-membership-button"
            >
              <span>VIEW MEMBERSHIP PLANS</span>
              <ArrowRight />
            </motion.button>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

const AnimatedSection = ({ children, delay = 0 }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

export default Home;